import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../utils/ThemeSwitcher';
import './Projects.css';
import ProjectLoadingOverlay from '../common/ProjectLoadingOverlay';
import TeamManagement from '../team/TeamManagement';
import ProjectSharing from '../team/ProjectSharing';
import { isTauri, fetchLocalProjects, createLocalProject, activateProjectStorage, softDeleteLocalProject, restoreLocalProject, permanentDeleteLocalProject, pickProjectFolder } from '../../utils/localProjects';
 
import { 
  FolderOpen, 
  Plus, 
  Trash2, 
  Calendar,
  Users,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  PlayCircle,
  RotateCcw,
  ChevronDown,
  LayoutGrid,
  List,
  Table,
  Search,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  Settings,
  Database,
  BarChart3,
  Brain,
  X,
  GripVertical,
  UsersRound,
  Share2,
  HardDrive,
} from 'lucide-react';
 
// API Base URL - uses environment variable in production
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com' // || 'http://localhost:8000';
 
// Workflow stages mapping (from WorkflowStepper)
const WORKFLOW_STAGES = {
  'setup': { label: 'Setup', color: '#6B7280', step: 0 },
  'load': { label: 'Load Data', color: '#2B8AFF', step: 1 },
  'quality': { label: 'Quality Check', color: '#8B5CF6', step: 2 },
  'clean': { label: 'Cleaning', color: '#F59E0B', step: 3 },
  'review': { label: 'Review', color: '#10B981', step: 4 },
  'report': { label: 'Report', color: '#06B6D4', step: 5 }
};
 
// Status badge component
const StatusBadge = ({ status, workflowStage }) => {
  const stage = WORKFLOW_STAGES[workflowStage] || WORKFLOW_STAGES['setup'];
  
  const getStatusInfo = () => {
    if (status === 'completed') return { label: 'Completed', color: '#10B981', bg: '#D1FAE5' };
    if (status === 'archived') return { label: 'Archived', color: '#6B7280', bg: '#d8d8d8ff' };
    return { label: stage.label, color: stage.color, bg: `${stage.color}20` };
  };
  
  const info = getStatusInfo();
  
  return (
    <span 
      className="status-badge"
      style={{ 
        backgroundColor: info.bg,
        color: info.color,
        border: `1px solid ${info.color}30`
      }}
    >
      <span className="status-dot" style={{ backgroundColor: info.color }} />
      {info.label}
    </span>
  );
};
 
// Resizable column header component
const ResizableHeader = ({ children, width, minWidth = 100, onResize, sortable, sortDirection, onSort }) => {
  const headerRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
 
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startX.current = e.clientX;
    startWidth.current = width;
  };
 
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const diff = e.clientX - startX.current;
      const newWidth = Math.max(minWidth, startWidth.current + diff);
      onResize(newWidth);
    };
 
    const handleMouseUp = () => {
      setIsResizing(false);
    };
 
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
 
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, minWidth, onResize]);
 
  return (
    <th 
      ref={headerRef}
      className={`resizable-header ${sortable ? 'sortable' : ''} ${isResizing ? 'resizing' : ''}`}
      style={{ width: `${width}px`, minWidth: `${minWidth}px` }}
      onClick={sortable ? onSort : undefined}
    >
      <div className="header-content">
        {children}
        {sortable && (
          <span className="sort-icon">
            {sortDirection === 'asc' ? <ArrowUp size={14} /> : 
             sortDirection === 'desc' ? <ArrowDown size={14} /> : 
             <ArrowUpDown size={14} />}
          </span>
        )}
      </div>
      <div 
        className="resize-handle"
        onMouseDown={handleMouseDown}
      />
    </th>
  );
};
 
// Progress-strip node gradient — matches WelcomeLanding for cross-page cohesion
const NODE_COLORS = ['#2563EB', '#5262c7', '#8b6c9b', '#c37535', '#D97706'];
 
// Status → human label (mirrors WelcomeLanding)
const pcStatusLabel = (s) => ({
  created: 'new',
  data_loaded: 'loaded',
  raw_quality_done: 'analysed',
  cleaning_in_progress: 'cleaning',
  cleaning_done: 'cleaned',
  cleaned_quality_done: 'assured',
  completed: 'complete',
  archived: 'archived',
}[s] || 'new');
 
// Status → color class on the card footer
const pcStatusClass = (s) => ({
  created: 'setup',
  data_loaded: 'loaded',
  raw_quality_done: 'analysed',
  cleaning_in_progress: 'cleaning',
  cleaning_done: 'cleaning',
  cleaned_quality_done: 'analysed',
  completed: 'complete',
  archived: 'archived',
}[s] || 'setup');
 
// Status → number of progress-strip nodes filled
const pcStageDone = (status) => ({
  created: 1,
  data_loaded: 2,
  raw_quality_done: 3,
  cleaning_in_progress: 3,
  cleaning_done: 4,
  cleaned_quality_done: 4,
  completed: 5,
}[status] || 1);
 
// Relative time formatter
const pcTimeAgo = (iso) => {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.max(1, Math.floor(d / 60))}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  if (d < 604800) return `${Math.floor(d / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};
 
// Project Card Component for Active/Closed views
const ProjectCard = ({ project, onSelect, onDelete, onRestore, onShare, isDeleted, isDarkMode, currentUser }) => {
  const done = pcStageDone(project.status);
  const isLocalProject = project.storage_mode === 'local';
  const rowCount = project.rowCount || project.row_count;
  const whenIso = project.updatedAt || project.updated_at || project.createdAt || project.created_at;
 
  return (
    <div
      className={`project-card ${isDarkMode ? 'dark' : 'light'} ${isDeleted ? 'deleted' : ''}`}
      onClick={() => !isDeleted && onSelect(project)}
    >
      {/* Hover-reveal actions */}
      <div className="pc-actions">
        {isDeleted ? (
          <button
            className="pc-action-btn restore"
            onClick={(e) => { e.stopPropagation(); onRestore(project.id); }}
            title="Restore project"
            aria-label="Restore project"
          >
            <RotateCcw size={13} />
          </button>
        ) : (
          <>
            {!isLocalProject && (
            <button
              className="pc-action-btn"
              onClick={(e) => { e.stopPropagation(); onShare && onShare(project); }}
              title="Share project"
              aria-label="Share project"
            >
              <Share2 size={13} />
            </button>
            )}
            <button
              className="pc-action-btn danger"
              onClick={(e) => { e.stopPropagation(); onDelete(project.id, project.name); }}
              title="Move to trash"
              aria-label="Move to trash"
            >
              <Trash2 size={13} />
            </button>
          </>
        )}
      </div>
 
      {/* Head */}
      <div className="pc-head">
        <h3 className="pc-title">
          {project.name}
          {isLocalProject && (
            <span title="Stored on this device only" style={{
              display: 'inline-flex', alignItems: 'center', gap: '3px',
              marginLeft: '8px', padding: '2px 7px', borderRadius: '5px',
              fontSize: '10px', fontWeight: 650, letterSpacing: '0.4px',
              textTransform: 'uppercase', verticalAlign: 'middle',
              background: 'rgba(37, 99, 235, 0.12)', color: '#2563EB',
            }}>
              <HardDrive size={10} /> local
            </span>
          )}
        </h3>
        {project.description && (
          <p className="pc-desc">{project.description}</p>
        )}
      </div>
 
      {/* 5-node progress strip — reflects Setup→Load→Analyse→Clean→Export */}
      {!isDeleted && (
        <div className="pc-progress" aria-label={`Progress: ${done} of 5 stages complete`}>
          {[0, 1, 2, 3, 4].map((n) => (
            <span
              key={n}
              className={`pc-progress-node ${n < done ? 'done' : ''}`}
              style={{ '--node-color': NODE_COLORS[n] }}
            />
          ))}
        </div>
      )}
 
      {/* Foot — status + time + rowcount */}
      <div className="pc-foot">
        <span className={`pc-status ${pcStatusClass(project.status)}`}>
          <span className="pc-status-dot" />
          {pcStatusLabel(project.status)}
        </span>
        <span className="pc-meta">
          {pcTimeAgo(whenIso)}
          {rowCount ? ` · ${rowCount.toLocaleString()} rows` : ''}
        </span>
      </div>
    </div>
  );
};
 
// Main Projects Component
const Projects = ({ onProjectSelect, currentProject, userEmail }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // active, all, closed
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'desc' });
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    storageMode: 'cloud',
    parentDir: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creatingSample, setCreatingSample] = useState(false);
  
  // Column widths state for resizable table
  const [columnWidths, setColumnWidths] = useState({
    name: 230,
    status: 130,
    people: 140,
    description: 260,
    rows: 110,
    dates: 140
  });
  
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  // Share modal state
  const [shareProject, setShareProject] = useState(null); // { id, name } of project being shared
 
  // Get current user info
  useEffect(() => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      if (userInfo) {
        setCurrentUser(userInfo);
      }
    } catch (e) {
      console.error('Error parsing user info:', e);
    }
  }, []);
 
  // Authentication check
  const checkAuthentication = () => {
    const token = localStorage.getItem('token');
    const userInfo = localStorage.getItem('userInfo');
    
    if (!token || !userInfo) {
      navigate('/login');
      return false;
    }
 
    try {
      const parsedUserInfo = JSON.parse(userInfo);
      if (!parsedUserInfo.email) {
        navigate('/login');
        return false;
      }
      return true;
    } catch (error) {
      navigate('/login');
      return false;
    }
  };
 
  useEffect(() => {
    if (userEmail && checkAuthentication()) {
      fetchProjects();
    }
  }, [userEmail]);
 
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!checkAuthentication()) return;
 
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
 
      const response = await fetch(`${API_BASE_URL}/api/projects?include_deleted=true`, {
        method: 'GET',
        headers: {
          'user_email': userInfo.email,
          'User-Email': userInfo.email,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
 
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          navigate('/login');
          return;
        }
        throw new Error(`HTTP ${response.status}: Failed to fetch projects`);
      }
 
      const data = await response.json();
      const cloudProjects = data.projects || [];
      const localProjects = await fetchLocalProjects(true);
      setProjects([...localProjects, ...cloudProjects]);
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Cloud failed: still show local projects so the desktop app works offline
      try {
        const localProjects = await fetchLocalProjects(true);
        if (localProjects.length > 0) {
          setProjects(localProjects);
          setError(null);
        } else {
          setError(error.message);
        }
      } catch (e2) {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };
 
  // Point the app at the right backend (cloud API or local engine) before activating
  const selectProject = async (project) => {
    try {
      await activateProjectStorage(project);
    } catch (e) {
      console.error('Failed to start local engine:', e);
      setError('Could not start the local engine for this project: ' + e.message);
      return;
    }
    onProjectSelect(project);
  };
 
  const createProject = async () => {
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }
 
    if (!checkAuthentication()) return;
 
    // Local storage mode: create through the on-device engine instead
    if (formData.storageMode === 'local') {
      if (!formData.parentDir) {
        setError('Choose a working folder for this local project');
        return;
      }
      try {
        setIsSubmitting(true);
        setError(null);
        const newProject = await createLocalProject({
          name: formData.name,
          description: formData.description,
          parentDir: formData.parentDir,
        });
        setProjects(prev => [newProject, ...prev]);
        setShowCreateForm(false);
        setFormData({ name: '', description: '', storageMode: 'cloud', parentDir: '' });
      } catch (error) {
        console.error('Error creating local project:', error);
        setError(error.message);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
 
    try {
      setIsSubmitting(true);
      setError(null);
 
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
 
      const response = await fetch(`${API_BASE_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'user_email': userInfo.email,
          'User-Email': userInfo.email
        },
        body: JSON.stringify(formData)
      });
 
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create project');
      }
 
      const newProject = await response.json();
      setProjects(prev => [newProject, ...prev]);
      setShowCreateForm(false);
      setFormData({ name: '', description: '', storageMode: 'cloud', parentDir: '' });
    } catch (error) {
      console.error('Error creating project:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };
 
  // Create (or fetch the existing) sample project and jump straight into it —
  // same as clicking any other project card. Idempotent server-side: repeat
  // clicks return the user's existing sample project instead of duplicating it.
  const trySampleData = async () => {
    if (!checkAuthentication()) return;
 
    try {
      setCreatingSample(true);
      setError(null);
 
      const token = localStorage.getItem('token');
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
 
      const response = await fetch(`${API_BASE_URL}/api/sample/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'user_email': userInfo?.email || '',
          'User-Email': userInfo?.email || ''
        }
      });
 
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          navigate('/login');
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Could not load sample data');
      }
 
      const data = await response.json();
      const sampleProject = data.project;
 
      setProjects(prev =>
        prev.some(p => p.id === sampleProject.id) ? prev : [sampleProject, ...prev]
      );
 
      onProjectSelect(sampleProject);
    } catch (error) {
      console.error('Error creating sample project:', error);
      setError(error.message);
    } finally {
      setCreatingSample(false);
    }
  };
 
  // Soft delete - moves to Closed tab
  const deleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Move "${projectName}" to Closed? You can restore it later.`)) {
      return;
    }
 
    if (!checkAuthentication()) return;
 
    const projToClose = projects.find(p => p.id === projectId);
    if (projToClose && projToClose.storage_mode === 'local') {
      try {
        await softDeleteLocalProject(projectId);
        setProjects(prev => prev.map(p => p.id === projectId ? { ...p, deletedAt: new Date().toISOString() } : p));
      } catch (error) {
        console.error('Error closing local project:', error);
        setError(error.message);
      }
      return;
    }
 
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/soft-delete`, {
        method: 'PATCH',
        headers: {
          'user_email': userInfo.email,
          'User-Email': userInfo.email,
          'Content-Type': 'application/json'
        }
      });
 
      if (!response.ok) {
        throw new Error('Failed to delete project');
      }
 
      // Update local state
      setProjects(prev => prev.map(p => 
        p.id === projectId 
          ? { ...p, deletedAt: new Date().toISOString() }
          : p
      ));
    } catch (error) {
      console.error('Error deleting project:', error);
      setError(error.message);
    }
  };
 
  // Restore from Closed
  const restoreProject = async (projectId) => {
    if (!checkAuthentication()) return;
 
    const projToRestore = projects.find(p => p.id === projectId);
    if (projToRestore && projToRestore.storage_mode === 'local') {
      try {
        await restoreLocalProject(projectId);
        setProjects(prev => prev.map(p => p.id === projectId ? { ...p, deletedAt: null } : p));
      } catch (error) {
        console.error('Error restoring local project:', error);
        setError(error.message);
      }
      return;
    }
 
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/restore`, {
        method: 'PATCH',
        headers: {
          'user_email': userInfo.email,
          'User-Email': userInfo.email,
          'Content-Type': 'application/json'
        }
      });
 
      if (!response.ok) {
        throw new Error('Failed to restore project');
      }
 
      // Update local state
      setProjects(prev => prev.map(p => 
        p.id === projectId 
          ? { ...p, deletedAt: null }
          : p
      ));
    } catch (error) {
      console.error('Error restoring project:', error);
      setError(error.message);
    }
  };
 
  // Permanent delete
  const permanentDeleteProject = async (projectId, projectName) => {
    const projToDelete = projects.find(p => p.id === projectId);
 
    // Local projects: the folder belongs to the user, so ask explicitly
    if (projToDelete && projToDelete.storage_mode === 'local') {
      if (!window.confirm(`Permanently remove "${projectName}" from DataQuad?`)) return;
      const removeFiles = window.confirm(
        `Also delete the project folder from disk?\n\n${projToDelete.folder}\n\nOK = delete the folder and all its files\nCancel = keep the folder, only remove the project from DataQuad`
      );
      try {
        await permanentDeleteLocalProject(projectId, removeFiles);
        setProjects(prev => prev.filter(p => p.id !== projectId));
      } catch (error) {
        console.error('Error deleting local project:', error);
        setError(error.message);
      }
      return;
    }
 
    if (!window.confirm(`Permanently delete "${projectName}"? This cannot be undone.`)) {
      return;
    }
 
    if (!checkAuthentication()) return;
 
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'user_email': userInfo.email,
          'User-Email': userInfo.email,
          'Content-Type': 'application/json'
        }
      });
 
      if (!response.ok) {
        throw new Error('Failed to delete project');
      }
 
      setProjects(prev => prev.filter(p => p.id !== projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
      setError(error.message);
    }
  };
 
  // Filter and sort projects
  const getFilteredProjects = useCallback(() => {
    let filtered = [...projects];
    
    // Filter by tab
    switch (activeTab) {
      case 'active':
        filtered = filtered.filter(p => !p.deletedAt);
        break;
      case 'closed':
        filtered = filtered.filter(p => p.deletedAt);
        break;
      case 'all':
      default:
        filtered = filtered.filter(p => !p.deletedAt);
        break;
    }
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) ||
        (p.description && p.description.toLowerCase().includes(query))
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle dates
      if (sortConfig.key === 'createdAt' || sortConfig.key === 'updatedAt') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      
      // Handle strings
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [projects, activeTab, searchQuery, sortConfig]);
 
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
 
  const handleColumnResize = (column, width) => {
    setColumnWidths(prev => ({ ...prev, [column]: width }));
  };
 
  const filteredProjects = getFilteredProjects();
  const activeCount = projects.filter(p => !p.deletedAt).length;
  const closedCount = projects.filter(p => p.deletedAt).length;
 
  if (loading) {
    return (
      <ProjectLoadingOverlay
        isDarkMode={isDarkMode}
        message="Loading projects"
        substep="Fetching your project list…"
      />
    );
  }
 
  return (
    <div className={`projects-container ${isDarkMode ? 'dark' : 'light'}`}>
      {/* Header */}
      <div className="projects-header">
        <div className="header-left">
          <div className="header-icon">
            <Settings size={24} />
          </div>
          <h1 className="projects-title">Projects</h1>
        </div>
        {/*
        <button 
          className="new-project-btn"
          onClick={() => setShowCreateForm(true)}
        >
          New
          <ChevronDown size={14} />
        </button>
        */}
      </div>
 
      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            <LayoutGrid size={14} />
            Active
            <span className="tab-count">{activeCount}</span>
          </button>
          <button 
            className={`tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <Table size={14} />
            All
          </button>
          <button 
            className={`tab ${activeTab === 'closed' ? 'active' : ''}`}
            onClick={() => setActiveTab('closed')}
          >
            <Trash2 size={14} />
            Closed
            {closedCount > 0 && <span className="tab-count">{closedCount}</span>}
          </button>
          <button 
            className={`tab ${activeTab === 'team' ? 'active' : ''}`}
            onClick={() => setActiveTab('team')}
          >
            <UsersRound size={14} />
            Team
          </button>
        </div>
        
        {activeTab !== 'team' && (
        <div className="tabs-actions">
          <div className="search-box">
            <Search size={14} />
            <input 
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>
                <X size={12} />
              </button>
            )}
          </div>
        </div>
        )}
      </div>
 
      {/* Error Message */}
      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
          <button onClick={() => setError(null)}><X size={14} /></button>
        </div>
      )}
 
      {/* Team Tab */}
      {activeTab === 'team' ? (
        <TeamManagement onNavigate={() => {}} />
      ) : (
      <>
      {/* Content */}
      {filteredProjects.length === 0 ? (
        <div className="empty-state">
          <FolderOpen size={48} />
          <h2>
            {activeTab === 'closed' 
              ? 'No closed projects' 
              : searchQuery 
                ? 'No projects found' 
                : 'No projects yet'}
          </h2>
          <p>
            {activeTab === 'closed'
              ? 'Projects you close will appear here'
              : searchQuery
                ? 'Try a different search term'
                : 'Create your first project to get started'}
          </p>
          {!searchQuery && activeTab !== 'closed' && (
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                className="empty-state-btn"
                onClick={() => setShowCreateForm(true)}
              >
                <Plus size={16} />
                Create Project
              </button>
              <button
                className="empty-state-btn-secondary"
                onClick={trySampleData}
                disabled={creatingSample}
              >
                <FolderOpen size={16} />
                {creatingSample ? 'Loading sample…' : 'Try Sample Data'}
              </button>
            </div>
          )}
        </div>
      ) : activeTab === 'all' ? (
        /* Table View for "All" tab */
        <div className="table-container">
          <table className="projects-table">
            <thead>
              <tr>
                <ResizableHeader
                  width={columnWidths.name}
                  minWidth={150}
                  onResize={(w) => handleColumnResize('name', w)}
                  sortable
                  sortDirection={sortConfig.key === 'name' ? sortConfig.direction : null}
                  onSort={() => handleSort('name')}
                >
                  <FileText size={14} />
                  Project name
                </ResizableHeader>
                <ResizableHeader
                  width={columnWidths.status}
                  minWidth={100}
                  onResize={(w) => handleColumnResize('status', w)}
                  sortable
                  sortDirection={sortConfig.key === 'status' ? sortConfig.direction : null}
                  onSort={() => handleSort('status')}
                >
                  Status
                </ResizableHeader>
                <ResizableHeader
                  width={columnWidths.people}
                  minWidth={120}
                  onResize={(w) => handleColumnResize('people', w)}
                >
                  <Users size={14} />
                  Owner
                </ResizableHeader>
                <ResizableHeader
                  width={columnWidths.description}
                  minWidth={150}
                  onResize={(w) => handleColumnResize('description', w)}
                >
                  Description
                </ResizableHeader>
                <ResizableHeader
                  width={columnWidths.rows}
                  minWidth={90}
                  onResize={(w) => handleColumnResize('rows', w)}
                  sortable
                  sortDirection={sortConfig.key === 'rowCount' ? sortConfig.direction : null}
                  onSort={() => handleSort('rowCount')}
                >
                  Rows
                </ResizableHeader>
                <ResizableHeader
                  width={columnWidths.dates}
                  minWidth={120}
                  onResize={(w) => handleColumnResize('dates', w)}
                  sortable
                  sortDirection={sortConfig.key === 'updatedAt' ? sortConfig.direction : null}
                  onSort={() => handleSort('updatedAt')}
                >
                  <Calendar size={14} />
                  Last active
                </ResizableHeader>
                <th className="actions-column">
                  <MoreHorizontal size={14} />
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project) => {
                const rowCount = project.rowCount || project.row_count;
                const whenIso = project.updatedAt || project.updated_at || project.createdAt || project.created_at;
                return (
                  <tr 
                    key={project.id}
                    className="table-row"
                    onClick={() => selectProject(project)}
                  >
                    <td className="name-cell">
                      <span className="project-name">{project.name}</span>
                      {project.storage_mode === 'local' && (
                        <span title="Stored on this device only" style={{
                          display: 'inline-flex', alignItems: 'center', gap: '3px',
                          marginLeft: '8px', padding: '1px 6px', borderRadius: '5px',
                          fontSize: '10px', fontWeight: 650, letterSpacing: '0.4px',
                          textTransform: 'uppercase',
                          background: 'rgba(37, 99, 235, 0.12)', color: '#2563EB',
                        }}>
                          <HardDrive size={10} /> local
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`pc-status ${pcStatusClass(project.status)}`}>
                        <span className="pc-status-dot" />
                        {pcStatusLabel(project.status)}
                      </span>
                    </td>
                    <td className="people-cell">
                      {currentUser && (
                        <div className="user-info">
                          <div className="user-avatar small">
                            {currentUser.name?.charAt(0).toUpperCase() || 
                             currentUser.email?.charAt(0).toUpperCase()}
                          </div>
                          <span>{currentUser.name || currentUser.email?.split('@')[0]}</span>
                        </div>
                      )}
                    </td>
                    <td className="description-cell">
                      <span className="description-text">
                        {project.description || '—'}
                      </span>
                    </td>
                    <td className="rows-cell">
                      {rowCount ? rowCount.toLocaleString() : <span className="row-dash">—</span>}
                    </td>
                    <td className="dates-cell">
                      {pcTimeAgo(whenIso)}
                    </td>
                    <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                      {project.storage_mode !== 'local' && (
                      <button 
                        className="row-action-btn share"
                        onClick={() => setShareProject({ id: project.id, name: project.name })}
                        title="Share project"
                      >
                        <Share2 size={14} />
                      </button>
                      )}
                      <button 
                        className="row-action-btn delete"
                        onClick={() => deleteProject(project.id, project.name)}
                        title="Move to closed"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {/* New project row */}
              <tr className="new-project-row" onClick={() => setShowCreateForm(true)}>
                <td colSpan={7}>
                  <Plus size={14} />
                  New project
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        /* Card View for Active/Closed tabs */
        <div className="cards-grid">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onSelect={selectProject}
              onDelete={deleteProject}
              onRestore={restoreProject}
              onShare={(p) => setShareProject({ id: p.id, name: p.name })}
              isDeleted={activeTab === 'closed'}
              isDarkMode={isDarkMode}
              currentUser={currentUser}
            />
          ))}
          {activeTab === 'active' && (
            <div 
              className={`project-card new-card ${isDarkMode ? 'dark' : 'light'}`}
              onClick={() => setShowCreateForm(true)}
            >
              <Plus size={24} />
              <span>New project</span>
            </div>
          )}
          {activeTab === 'active' && (
            <div
              className={`project-card new-card ${isDarkMode ? 'dark' : 'light'}`}
              onClick={creatingSample ? undefined : trySampleData}
              style={creatingSample ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
            >
              <FolderOpen size={24} />
              <span>{creatingSample ? 'Loading sample…' : 'Try Sample Data'}</span>
            </div>
          )}
        </div>
      )}
      </>
      )}
 
      {/* Create Project Modal */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={() => setShowCreateForm(false)}>
          <div 
            className={`modal-content ${isDarkMode ? 'dark' : 'light'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Create New Project</h2>
              <button 
                className="modal-close"
                onClick={() => setShowCreateForm(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Project Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter project name"
                  autoFocus
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your project (optional)"
                  rows={3}
                />
              </div>
 
              {/* Storage choice: Cloud (default) or Local device */}
              <div className="form-group">
                <label>Data storage</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {[
                    { mode: 'cloud', title: 'DataQuad Cloud', desc: 'Secure cloud storage and processing. Access from anywhere, share with your team.', enabled: true },
                    { mode: 'local', title: 'This device only', desc: isTauri() ? 'Data never leaves this machine. For DUAs, NDAs, and data protection laws.' : 'Available in the DataQuad desktop app only.', enabled: isTauri() },
                  ].map(opt => (
                    <div
                      key={opt.mode}
                      onClick={() => opt.enabled && setFormData(prev => ({ ...prev, storageMode: opt.mode }))}
                      style={{
                        flex: 1, padding: '12px 14px', borderRadius: '10px', cursor: opt.enabled ? 'pointer' : 'not-allowed',
                        opacity: opt.enabled ? 1 : 0.5,
                        border: formData.storageMode === opt.mode ? '2px solid #2563EB' : `1px solid ${isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                        background: formData.storageMode === opt.mode ? 'rgba(37, 99, 235, 0.06)' : 'transparent',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 650, marginBottom: '4px' }}>
                        {opt.mode === 'local' ? <HardDrive size={14} /> : <Share2 size={14} />}
                        {opt.title}
                      </div>
                      <div style={{ fontSize: '11.5px', lineHeight: 1.45, opacity: 0.75 }}>{opt.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
 
              {formData.storageMode === 'local' && (
                <div className="form-group">
                  <label>Working folder</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={async () => {
                        const dir = await pickProjectFolder();
                        if (dir) setFormData(prev => ({ ...prev, parentDir: dir }));
                      }}
                    >
                      Choose folder...
                    </button>
                    <span style={{ fontSize: '12px', opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {formData.parentDir || 'No folder chosen'}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', opacity: 0.65, marginTop: '6px', lineHeight: 1.5 }}>
                    A normal folder, a synced folder (Dropbox, OneDrive, Google Drive), or an unlocked encrypted vault (Cryptomator).
                    Project data and processing stay on this machine. Storage mode cannot be changed after creation.
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormData({ name: '', description: '' });
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={createProject}
                disabled={isSubmitting || !formData.name.trim()}
              >
                {isSubmitting ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* Project Sharing Modal */}
      <ProjectSharing
        projectId={shareProject?.id}
        projectName={shareProject?.name}
        isOpen={!!shareProject}
        onClose={() => setShareProject(null)}
      />
    </div>
  );
};
 
export default Projects;